import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Box, Button, CircularProgress, Typography } from '@mui/material'

import { Client, clientsApi } from '../services/api'
import { isJobSearchEligible } from '../state/clients'
import ErrorState from '../components/common/ErrorState'
import SignalsPage from '../components/signals/SignalsPage'

/**
 * MWA-421 — a client's Signals page for staff, at /clients/:id/signals beside
 * their Front Door.
 *
 * Gated like the Front Door: it opens for a client who has reached the market,
 * and logging a new signal also waits for the client to still be on it, since a
 * finished engagement stays readable but gains nothing new.
 */
export default function ClientSignals() {
  const { id: clientId } = useParams<{ id: string }>()
  const [client, setClient] = useState<Client | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadClient = useCallback(async () => {
    if (!clientId) return
    setLoading(true)
    try {
      setClient(await clientsApi.get(clientId))
      setError(null)
    } catch (err) {
      console.error('Failed to load the client for Signals:', err)
      setError(`get-client: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    loadClient()
  }, [loadClient])

  if (!clientId) return null

  if (loading && !client) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 7 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  // A failed read must not read as "this client is not on the market".
  if (error || !client) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8 }}>
        <ErrorState
          title="Couldn't load this client"
          description="Signals needs the client record to know whether it opens. Nothing was changed, and retrying is safe."
          detail={error ?? undefined}
          onRetry={loadClient}
        />
      </Box>
    )
  }

  if (!isJobSearchEligible(client)) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          This client isn&rsquo;t on the market yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Signals opens once a client reaches the Market Ready stage.
        </Typography>
        <Button component={RouterLink} to={`/clients/${clientId}`} variant="outlined">
          View client record
        </Button>
      </Box>
    )
  }

  return (
    <SignalsPage
      clientId={clientId}
      clientName={client.name || client.email || ''}
      audience="staff"
      logDisabledReason={
        client.workflowStage === 'market_ready' ? undefined : 'Client is no longer on the market'
      }
      headerExtra={
        <Button component={RouterLink} to={`/clients/${clientId}/opportunities`} size="small">
          Front Door
        </Button>
      }
    />
  )
}
